import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import CustomProposalForm from '../contact-multi-channel-engagement-hub/components/CustomProposalForm';
import Icon from '../../components/AppIcon';

const RequestProposal = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Request Custom Proposal | Northstar Analytics</title>
        <meta 
          name="description" 
          content="Request a detailed custom proposal from Northstar Analytics. Receive tailored data strategy solutions with investment breakdown and implementation timeline." 
        />
        <meta name="keywords" content="request proposal, custom data strategy, project quote, data transformation proposal" />
        <meta property="og:title" content="Request Custom Proposal | Northstar Analytics" />
        <meta property="og:description" content="Get a detailed proposal tailored to your specific data strategy requirements." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/request-proposal" />
      </Helmet>
      <Header />
      <main className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-secondary/5 to-background py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 rounded-full mb-6">
              <Icon name="FileText" size={32} className="text-secondary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
              Request a Custom Proposal
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Tell us about your data strategy needs, and we'll prepare a comprehensive proposal tailored to your specific requirements, including detailed scope, investment breakdown, and implementation timeline.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={20} className="text-secondary" />
                <span>2-3 business days delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="FileCheck" size={20} className="text-secondary" />
                <span>Detailed scope of work</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="DollarSign" size={20} className="text-secondary" />
                <span>Transparent pricing</span>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included Section */}
        <section className="py-12 bg-surface border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-primary mb-8 text-center">
              What's Included in Your Proposal
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="FileText" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Detailed Scope</h3>
                <p className="text-sm text-muted-foreground">
                  Comprehensive breakdown of deliverables, methodologies, and approach.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="DollarSign" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Investment Breakdown</h3>
                <p className="text-sm text-muted-foreground">
                  Transparent pricing with clear cost allocation and payment terms.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Calendar" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Timeline & Milestones</h3>
                <p className="text-sm text-muted-foreground">
                  Realistic project timeline with key milestones and deliverable dates.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Users" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Team Allocation</h3>
                <p className="text-sm text-muted-foreground">
                  Information about the expert team members assigned to your project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Proposal Form */}
        <CustomProposalForm />

        {/* Process Timeline */}
        <section className="py-12 bg-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-primary mb-8 text-center">
              Our Proposal Process
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-secondary">1</span>
                </div>
                <h4 className="font-semibold text-primary mb-2">Submit Requirements</h4>
                <p className="text-sm text-muted-foreground">
                  Complete the form with your project details and specific needs
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-secondary">2</span>
                </div>
                <h4 className="font-semibold text-primary mb-2">Expert Review</h4>
                <p className="text-sm text-muted-foreground">
                  Our team analyzes your requirements and prepares a tailored solution
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-secondary">3</span>
                </div>
                <h4 className="font-semibold text-primary mb-2">Receive Proposal</h4>
                <p className="text-sm text-muted-foreground">
                  Get your detailed proposal within 2-3 business days
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RequestProposal;